import { z } from 'zod'
import type { ZodObject, ZodRawShape } from 'zod'
import { LoanEventType } from '@viilu-web/shared/enums/loans'
import { getRequiredFields } from 'util/getZodSchemaRequiredFields'
import type { IntlShape } from 'react-intl'
import { numberField } from 'util/zodSchemas'
import type { LoanCalculation, LoanEvent } from '@viilu-web/shared/types/loans'

export const createEventFormBaseSchema = z.object({
	eventDate: z.string().trim(),
	registerDate: z.string().trim(),
	shareGroupUUID: z.string().trim().optional().nullable(),
	type: z.nativeEnum(LoanEventType),
	amount: z.number(),
	amountWithExpenses: z.number(),
	balanceAdjustmentAmount: z.number().optional().nullable(),
	scaledBalanceAdjustmentAmount: z.number().optional().nullable(),
	adjustedAmount: z.number().optional().nullable(),
	units: z.number().optional().nullable(),
	explanation: z.string().trim().optional().nullable(),
	interestAmount: z.number().optional().nullable(),
	interestStartDate: z.string().trim().optional().nullable(),
	interestEndDate: z.string().trim().optional().nullable(),
	bankExpenseAmount: z.number().optional().nullable(),
	otherExpenseAmount: z.number().optional().nullable(),
	currency: z.string(),
	sharegroupDebtRepayments: z.string().array().optional().nullable(),
})

export const requiredCreateEventFields = getRequiredFields(
	createEventFormBaseSchema,
)

export const createEventFormSchema = (intl: IntlShape) => {
	const s = createEventFormBaseSchema

	return s.extend({
		eventDate: s.shape.eventDate.date(
			intl.formatMessage({
				defaultMessage: 'Tapahtumapäivä vaaditaan.',
				description: 'Error message for required event date',
			}),
		),
		registerDate: s.shape.registerDate.date(
			intl.formatMessage({
				defaultMessage: 'Kirjauspäivä vaaditaan.',
				description: 'Error message for required register date',
			}),
		),
		interestAmount: numberField(intl, {
			required: { value: false },
			allowNegative: { value: false },
		}),
		bankExpenseAmount: numberField(intl, {
			required: { value: false },
			allowNegative: { value: false },
		}),
		otherExpenseAmount: numberField(intl, {
			required: { value: false },
			allowNegative: { value: false },
		}),
	})
}

export const createEventFormSchemaBasedOnEventType = (
	intl: IntlShape,
	eventType: LoanEventType,
	allEvents: LoanEvent[],
	loanCalculation?: LoanCalculation,
) => {
	let s = createEventFormSchema(intl)
	let baseSchema: ZodObject<ZodRawShape> | undefined

	switch (eventType) {
		case LoanEventType.LOAN_DRAW: {
			const drawableAmount =
				(loanCalculation?.originalLoanAmount || 0) -
				(loanCalculation?.loanDrawnAmount || 0)
			const maxAmount = drawableAmount > 0 ? drawableAmount : 0
			baseSchema = s.extend({
				amount: numberField(intl, {
					required: { value: true },
					allowNegative: { value: false },
					max: {
						value: maxAmount,
					},
					min: { value: 1 },
				}),
			})
			break
		}
		case LoanEventType.LOAN_BANK_REPAYMENT: {
			baseSchema = s
				.extend({
					amount: numberField(intl, {
						required: { value: true },
						allowNegative: { value: false },
						min: { value: 1 },
					}),
				})
				.omit({ shareGroupUUID: true })
			break
		}
		case LoanEventType.INTEREST_CHANGE: {
			baseSchema = s.extend({
				amount: numberField(intl, {
					required: { value: true },
					allowNegative: { value: true },
				}),
			})
			break
		}
		case LoanEventType.SHAREGROUP_DEBT_REPAYMENT: {
			baseSchema = s.extend({
				amount: numberField(intl, {
					required: { value: true },
					allowNegative: { value: false },
					min: { value: 1 },
				}),
				shareGroupUUID: z
					.string()
					.trim()
					.min(1, {
						message: intl.formatMessage({
							defaultMessage: 'Huoneisto on valittava.',
							description: 'Error message for required apartment',
						}),
					}),
				units: numberField(intl, {
					required: { value: true },
					allowNegative: { value: false },
					integer: { value: true },
				}),
			})
			break
		}
		case LoanEventType.COLLECTED_SHAREGROUP_BANK_REPAYMENT: {
			baseSchema = s.extend({
				amount: numberField(intl, {
					required: { value: true },
					allowNegative: { value: false },
					min: { value: 1 },
				}),
				sharegroupDebtRepayments: z
					.string()
					.array()
					.min(1, {
						message: intl.formatMessage({
							defaultMessage: 'Lyhennyksiä on valittava vähintään 1.',
							description:
								'Error message for minimum selected sharegroup debt repayments',
						}),
					}),
			})
			break
		}
		case LoanEventType.STARTING_BALANCE: {
			baseSchema = s.extend({
				amount: numberField(intl, {
					required: { value: true },
					allowNegative: { value: false },
				}),
			})
			break
		}
		case LoanEventType.STARTING_COLLECTED_PAYMENTS: {
			baseSchema = s.extend({
				amount: numberField(intl, {
					required: { value: true },
					allowNegative: { value: false },
				}),
			})
			break
		}
		case LoanEventType.STARTING_SURPLUS_OR_DEFICIT: {
			baseSchema = s.extend({
				amount: numberField(intl, {
					required: { value: true },
					allowNegative: { value: true },
				}),
			})
			break
		}
	}

	// TODO: change zod resolver to accept data in context so we dont need to take in
	// relevant data in function parameters
	return baseSchema.superRefine((data, ctx) => {
		if (!allEvents) return
		const uniqueEvents = [
			LoanEventType.STARTING_BALANCE,
			LoanEventType.STARTING_SURPLUS_OR_DEFICIT,
			LoanEventType.STARTING_COLLECTED_PAYMENTS,
		]

		// Check duplicates only for certain events
		if (!uniqueEvents.includes(data.type)) return

		const duplicate = allEvents.some((e) => e.type === data.type)

		if (duplicate) {
			let message = ''
			if (data.type === LoanEventType.STARTING_BALANCE) {
				message = intl.formatMessage({
					defaultMessage:
						'Alkutilanteen saldo tapahtumia voi olla lainalla vain yksi.',
					description:
						'Error message when unique event is already made: starting balance',
				})
			} else if (data.type === LoanEventType.STARTING_SURPLUS_OR_DEFICIT) {
				message = intl.formatMessage({
					defaultMessage:
						'Alkutilanteen yli- tai alijäämä tapahtumia voi olla lainalla vain yksi.',
					description:
						'Error message when unique event is already made: starting collected payments',
				})
			} else if (data.type === LoanEventType.STARTING_COLLECTED_PAYMENTS) {
				message = intl.formatMessage({
					defaultMessage:
						'Alkutilanteen kerätyt vastikkeet tapahtumia voi olla lainalla vain yksi.',
					description:
						'Error message when unique event is already made: starting surplus or deficit',
				})
			}
			ctx.addIssue({
				path: ['type'],
				message,
				code: z.ZodIssueCode.custom,
			})
		}
	})
}

export type LoanEventCreateFormInputs = z.infer<
	typeof createEventFormBaseSchema
>
